import { useCallback, useEffect, useMemo, useState } from "react";

import { fetchAdminDashboard, updateMemberStatus } from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminDashboardData } from "@/features/admin/types";

interface DashboardTabProps {
  onDashboardLoaded?: (dashboard: AdminDashboardData) => void;
  onOpenMembersTab?: () => void;
}

const orderStatusMap: Record<string, { label: string; cls: string }> = {
  en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-600" },
  confirme: { label: "Confirmé", cls: "bg-blue-100 text-blue-600" },
  expedie: { label: "Expédié", cls: "bg-purple-100 text-purple-600" },
  livre: { label: "Livré", cls: "bg-emerald-100 text-emerald-600" },
  annule: { label: "Annulé", cls: "bg-red-100 text-red-500" }
};

export default function DashboardTab({ onDashboardLoaded, onOpenMembersTab }: DashboardTabProps) {
  const { request } = useAdminSession();
  const [chartType, setChartType] = useState<"revenus" | "membres" | "commandes">("revenus");
  const [dashboard, setDashboard] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextDashboard = await fetchAdminDashboard(request);
      setDashboard(nextDashboard);
      onDashboardLoaded?.(nextDashboard);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger le tableau de bord";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [onDashboardLoaded, request]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  };

  const handleValidate = async (memberId: string, status: "actif" | "refuse") => {
    try {
      await updateMemberStatus(request, memberId, status);
      showToast(status === "actif" ? "Adhésion validée avec succès" : "Adhésion refusée");
      await loadDashboard();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Action impossible";
      setError(message);
    }
  };

  const statCards = useMemo(() => {
    if (!dashboard) {
      return [];
    }

    return [
      {
        label: "Membres actifs",
        value: dashboard.stats.membresActifs,
        total: dashboard.stats.totalMembres,
        icon: "ri-team-line",
        light: "bg-teal-50",
        textColor: "text-[#1A8C7A]",
        trend: `${dashboard.stats.totalMembres} au total`,
        trendUp: true
      },
      {
        label: "Adhésions en attente",
        value: dashboard.stats.adhesionsEnAttente,
        icon: "ri-user-add-line",
        light: "bg-amber-50",
        textColor: "text-amber-600",
        trend: "À traiter",
        trendUp: true
      },
      {
        label: "Commandes ce mois",
        value: dashboard.stats.commandesMois,
        icon: "ri-shopping-cart-2-line",
        light: "bg-orange-50",
        textColor: "text-[#E8521A]",
        trend: `${dashboard.stats.totalProduits} produits`,
        trendUp: true
      },
      {
        label: "Revenus ce mois",
        value: `${(dashboard.stats.revenuMois / 1000).toFixed(0)}k FCFA`,
        icon: "ri-money-cny-circle-line",
        light: "bg-emerald-50",
        textColor: "text-emerald-600",
        trend: `${dashboard.stats.evenementsAVenir} événements à venir`,
        trendUp: true
      }
    ];
  }, [dashboard]);

  const chartData = useMemo(() => {
    if (!dashboard) {
      return [];
    }

    return dashboard.monthlyStats.map((stat) => ({
      ...stat,
      value:
        chartType === "revenus"
          ? stat.revenus
          : chartType === "membres"
            ? stat.membres * 10000
            : stat.commandes * 10000,
      display:
        chartType === "revenus"
          ? `${(stat.revenus / 1000).toFixed(0)}k`
          : chartType === "membres"
            ? stat.membres
            : stat.commandes
    }));
  }, [chartType, dashboard]);

  const maxVal = Math.max(...chartData.map((item) => item.value), 1);
  const activeRate = dashboard && dashboard.stats.totalMembres > 0
    ? Math.round((dashboard.stats.membresActifs / dashboard.stats.totalMembres) * 100)
    : 0;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 rounded-xl bg-gray-200 animate-pulse"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl p-5 border border-gray-100 h-40 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="bg-white rounded-2xl border border-red-100 p-6">
        <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Tableau de bord</h1>
        <p className="text-red-500 text-sm mt-3">{error ?? "Impossible de charger les données."}</p>
        <button
          onClick={() => void loadDashboard()}
          className="mt-4 px-4 py-2 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg bg-emerald-500 text-white text-sm font-medium flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-lg"></i>
          {toast}
        </div>
      )}

      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Tableau de bord</h1>
        <p className="text-gray-500 text-sm mt-1">Vue d&apos;ensemble opérationnelle de l&apos;administration ASC Sonatel</p>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-2xl p-5 border border-gray-100">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${card.light}`}>
                <i className={`${card.icon} text-xl ${card.textColor}`}></i>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${card.trendUp ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                {card.trend}
              </span>
            </div>
            <div className="text-2xl font-bold text-[#1A1A1A]">{card.value}</div>
            <div className="text-sm text-gray-500 mt-1">{card.label}</div>
            {card.total ? (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Actifs</span>
                  <span>{activeRate}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#1A8C7A] rounded-full" style={{ width: `${activeRate}%` }}></div>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white rounded-2xl p-4 md:p-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <h2 className="font-bold text-[#1A1A1A]">Évolution mensuelle</h2>
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 self-start sm:self-auto">
              {(["revenus", "membres", "commandes"] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setChartType(type)}
                  className={`px-2 md:px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                    chartType === type ? "bg-white text-[#E8521A] shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end gap-1 md:gap-3 h-36 md:h-44">
            {chartData.map((item) => (
              <div key={item.mois} className="flex-1 flex flex-col items-center gap-1 md:gap-2">
                <span className="hidden sm:block text-xs text-gray-400 font-medium">{item.display}</span>
                <div className="w-full">
                  <div
                    className="w-full rounded-t-lg transition-all duration-500 bg-gradient-to-t from-[#E8521A] to-[#f07a4a]"
                    style={{ height: `${(item.value / maxVal) * 120}px`, minHeight: "8px" }}
                  ></div>
                </div>
                <span className="text-xs text-gray-500">{item.mois}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100">
          <h2 className="font-bold text-[#1A1A1A] mb-4">Répartition sports</h2>
          <div className="space-y-3">
            {dashboard.sportDistribution.length > 0 ? (
              dashboard.sportDistribution.slice(0, 5).map((sport) => (
                <div key={sport.sport}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{sport.sport}</span>
                    <span className="font-semibold text-[#1A1A1A]">{sport.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#1A8C7A]"
                      style={{ width: `${(sport.count / dashboard.sportDistribution[0].count) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">Aucune donnée sport disponible.</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1A1A1A]">Adhésions en attente</h2>
            <button
              onClick={onOpenMembersTab}
              className="bg-amber-100 text-amber-600 text-xs font-bold px-2.5 py-1 rounded-full"
            >
              {dashboard.stats.adhesionsEnAttente} en attente
            </button>
          </div>
          <div className="space-y-3">
            {dashboard.pendingMembers.length > 0 ? (
              dashboard.pendingMembers.map((member) => (
                <div key={member.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-orange-50 transition-colors">
                  <img src={member.avatar ?? ""} alt={member.nom} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1A1A1A] truncate">{member.prenom} {member.nom}</div>
                    <div className="text-xs text-gray-500 truncate">{member.departement ?? "Département non renseigné"}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => void handleValidate(member.id, "actif")}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                    >
                      <i className="ri-check-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => void handleValidate(member.id, "refuse")}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-400 py-6 text-center">Aucune adhésion en attente.</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1A1A1A]">Commandes récentes</h2>
          </div>
          <div className="space-y-3">
            {dashboard.recentOrders.length > 0 ? (
              dashboard.recentOrders.map((order) => {
                const mapped = orderStatusMap[order.statut] ?? orderStatusMap.en_attente;

                return (
                  <div key={order.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-100 flex-shrink-0">
                      <i className="ri-shopping-bag-line text-[#E8521A]"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#1A1A1A] truncate">{order.id}</div>
                      <div className="text-xs text-gray-500 truncate">{order.membre}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-[#1A1A1A]">{(order.total / 1000).toFixed(0)}k</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${mapped.cls}`}>{mapped.label}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-gray-400 py-6 text-center">Aucune commande récente.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
